import * as React from 'react';
import * as uuid from 'uuid/v4';
import * as moment from 'moment';
import { each, find, get, isArray, keys, omit } from 'lodash';
import { FieldArray, getFormValues, reduxForm } from 'redux-form';
import DayPicker from 'react-day-picker';
import { Col, Grid, Row } from 'react-flexbox-grid';
import CloseIcon from 'material-ui/svg-icons/navigation/close';
import Button from '@inqool/uas-web-core/es/components/controls/Button';
import { compose, lifecycle, withHandlers, withProps, withState, withStateHandlers } from 'recompose';

import { withNotifications } from '@components/hoc/withNotifications';
import { Loader } from '@components/loader';
import { required, time } from '@utils/validators';
import { PeriodTypesEnum } from '@modules/hr-templates/periods/_periodsEnums';
import {
  AutoComplete,
  AutoCompleteField,
  Input,
  InputField,
  RemoteAutoComplete,
  RemoteAutoCompleteField,
  Time,
  TimeField
} from '@components/form';
import IconButton from 'material-ui/IconButton';
import { days, daysShort, months } from './_overviewEnums';
import { deleteEvidence, getAllEvidences, saveWorkplanEvidence } from './_myOverviewActions';
import { connect } from 'react-redux';
import { ModalButton } from '@components/modalButton/ModalButton';
import { OverviewAddPeriodsModal } from './OverviewAddPeriodsModal';
import { getConfig } from '@components/provider/configuration/_configurationActions';

const parsePeriod = (value, date) =>
  date
    .hours(value.substring(0, 2))
    .minutes(value.substring(3, 5))
    .toDate();

const validPeriod = (name, periods) => {
  if (!periods) {
    return undefined;
  }
  // remove from & to sufix from field name
  name = name.substring(0, name.indexOf('.'));
  const date = moment();
  periods = periods.map((period, index) => {
    return {
      name: `periods[${index}]`,
      from: get(period, 'from') ? parsePeriod(period.from, date) : null,
      to: get(period, 'to') ? parsePeriod(period.to, date) : null
    };
  });
  const isError = periods.some((period, index) => {
    const currentField = period.name === name;
    if (!currentField) {
      return false;
    }
    const nextPeriod = periods[index + 1];
    if (!nextPeriod) {
      return false;
    }
    if (period.to > nextPeriod.from) {
      return true;
    }
    return false;
  });
  return isError ? 'Intervaly musí být seřazeny posloupně a nepřekrývat se' : undefined;
};

const addPeriods = fields => ({ type: { periods } }) => {
  periods.forEach(({ from, to, type }) => fields.push({ from, to, type }));
};

const enhance = compose<any, any>(
  withNotifications({ saveWorkplanEvidence, deleteEvidence, getConfig }),
  withState('isFetching', 'toggleFetching', false),
  withState('mode', 'changeMode', 'creating'),
  withState('modifiers', 'fetchModifiers', {}),
  withState('daysWithWork', 'fetchDaysWithWork', []),
  withState('dataItems', 'fetchItems', []),
  withState('monthSelected', 'setMonthSelected', new Date()),
  withHandlers({
    fetchAllEvidences: ({ fetchModifiers, toggleFetching, fetchDaysWithWork, fetchItems, monthSelected }) => async (
      employeeId?: string
    ) => {
      toggleFetching(true);
      if (!employeeId) {
        fetchModifiers({});
        fetchDaysWithWork({});
      }
      const response = await getAllEvidences(employeeId, monthSelected);
      const items = get(response, 'items', []);
      fetchModifiers({
        daysWithWork: items.map(item => new Date(get(item, 'periods.[0].from')))
      });
      fetchDaysWithWork(
        get(response, 'items', []).map(item => ({
          ...item,
          ymd: moment.utc(get(item, 'periods.[0].from')).format('YYYY-MM-DD')
        }))
      );
      fetchItems(items);
      toggleFetching(false);
    }
  }),
  withHandlers({
    onSubmit: ({
      toggleFetching,
      fetchAllEvidences,
      changeMode,
      employeeId,
      saveWorkplanEvidenceNotify
    }) => async values => {
      toggleFetching(true);
      let shift = 0;
      const newValues = {
        ...omit(values, ['date']),
        employee: { id: employeeId },
        periods: values.periods.map(period => {
          const periodParsed = {
            ...period,
            from: moment
              .utc(values.date)
              .hours(period.from.substring(0, 2))
              .minutes(period.from.substring(3, 5))
              .toDate(),
            to: moment
              .utc(values.date)
              .hours(period.to.substring(0, 2))
              .minutes(period.to.substring(3, 5))
              .toDate()
          };

          periodParsed.from.setUTCDate(periodParsed.from.getUTCDate() + shift);
          periodParsed.to.setUTCDate(periodParsed.to.getUTCDate() + shift);

          // in case of interval extending to the next day
          // edge case is same hours, but the from time is earlier by minutes
          if (periodParsed.from >= periodParsed.to) {
            shift += 1;
            periodParsed.to.setUTCDate(periodParsed.to.getUTCDate() + shift);
          }

          return omit(periodParsed, 'id');
        })
      };
      await saveWorkplanEvidenceNotify(newValues).then(() => {
        fetchAllEvidences(employeeId);
      }, () => false);
      toggleFetching(false);
      changeMode('updating');
    }
  }),
  reduxForm({
    form: 'employeeHours'
  }),
  withHandlers({
    setForm: ({ change }) => values => {
      each(keys(values), key => change(key, get(values, key)));
    }
  }),
  withHandlers({
    resetForm: ({ setForm, changeMode }) => (day = new Date()) => {
      changeMode('creating');
      setForm({
        date: moment.utc(day).format('YYYY-MM-DD'),
        type: null,
        periods: null,
        note: null,
        id: uuid()
      });
    },
    loadEvidenceToForm: ({ setForm, changeMode }) => evidence => {
      changeMode('updating');
      setForm({
        date: get(evidence, 'ymd'),
        type: get(evidence, 'type', null),
        periods: get(evidence, 'periods', []).map(period => ({
          ...period,
          from: moment.utc(period.from).format('HH:mm'),
          to: moment.utc(period.to).format('HH:mm')
        })),
        note: get(evidence, 'note', null),
        id: get(evidence, 'id')
      });
    }
  }),
  withStateHandlers<any, any, any>(
    {
      selectedDay: new Date()
    },
    {
      setSelectedDay: (_state, { daysWithWork, loadEvidenceToForm, resetForm }) => day => {
        const existingEvidence = find(daysWithWork, {
          ymd: moment.utc(day).format('YYYY-MM-DD')
        });
        if (!existingEvidence) {
          resetForm(day);
        } else {
          loadEvidenceToForm(existingEvidence);
        }

        return {
          selectedDay: day
        };
      }
    }
  ),
  withHandlers({
    renderType: ({ dataItems }) => day => {
      const dayMoment = moment(day);
      const foundData = dataItems.find(({ periods }) => dayMoment.isSame(get(periods[0], 'from'), 'day'));
      return get(foundData, 'type.name');
    }
  }),
  lifecycle<any, any>({
    componentDidUpdate({ employeeId: oldEmployeeId, monthSelected }: any) {
      const { fetchAllEvidences, employeeId: newEmployeeId, monthSelected: newSelectedDay } = this.props;
      const isSameMonth = moment(monthSelected).isSame(newSelectedDay, 'month');
      if (newEmployeeId !== oldEmployeeId || !isSameMonth) {
        fetchAllEvidences(newEmployeeId);
      }
    },
    async componentDidMount() {
      const { getConfigNotify } = this.props;
      try {
        const config = await getConfigNotify();
        this.setState({ config });
      } catch {
        this.setState({ config: {} });
      }
    }
  }),
  withProps(({ config }) => {
    return {
      compactAttendance: get(config, 'extensions.compact-attendance') === 'true'
    };
  }),
  connect<any, any, any>(
    (state, { employeeId }) => {
      const periods = get(getFormValues('employeeHours')(state), 'periods', []);
      return {
        canSaveWorkplan: employeeId && periods && isArray(periods) && periods.length > 0,
        periods
      };
    },
    {}
  ),
  withHandlers({
    deleteSingleEvidence: ({
      toggleFetching,
      fetchAllEvidences,
      deleteEvidenceNotify,
      selectedDay,
      resetForm,
      employeeId
    }) => async id => {
      toggleFetching(true);
      const deleted = await deleteEvidenceNotify(id);
      if (deleted) {
        await fetchAllEvidences(employeeId);
        resetForm(selectedDay);
      }
      toggleFetching(false);
    }
  })
);

const renderPeriods = ({ fields, meta: { error }, validPeriods }) => {
  return (
    <>
      {fields.map((member, index) => (
        <div key={member} style={{ background: '#f9f9f9', padding: 10, margin: '10px 0' }}>
          <h3 style={{ position: 'relative', margin: 0 }}>
            Interval č. {index + 1}
            <IconButton
              tooltip="Odstranit interval"
              onClick={() => {
                fields.remove(index);
              }}
              style={{
                position: 'absolute',
                right: 10,
                padding: 0,
                width: 30,
                height: 30
              }}
            >
              <CloseIcon />
            </IconButton>
          </h3>
          <Row>
            <Col xs={12} md={6}>
              <TimeField
                component={Time}
                id="from"
                name={`${member}.from`}
                label="Od *"
                validate={[time, validPeriods]}
              />
            </Col>
            <Col xs={12} md={6}>
              <TimeField component={Time} id="to" name={`${member}.to`} label="Do *" validate={[time, validPeriods]} />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <AutoCompleteField
                component={AutoComplete}
                id="type"
                name={`${member}.type`}
                label="Typ práce *"
                items={PeriodTypesEnum}
                fullWidth={true}
                labelFunction={o => get(o, 'label')}
                valueFunction={o => get(o, 'id')}
                validate={[required]}
              />
            </Col>
          </Row>
        </div>
      ))}
      <div className="buttons">
        <Button
          type="button"
          onClick={() => {
            fields.push();
          }}
          primary={true}
          label={`Přidat ${fields.length > 0 ? 'další ' : ''}interval`}
        />
        <ModalButton
          label="Přidat intervaly ze šablony"
          style={{ margin: '16px 0 16px 16px' }}
          ModalComponent={OverviewAddPeriodsModal}
          onSubmit={addPeriods(fields)}
        />
      </div>
      {error && <div className="error">{error}</div>}
    </>
  );
};

const View: React.SFC<any> = ({
  handleSubmit,
  isFetching,
  modifiers,
  selectedDay,
  setSelectedDay,
  mode,
  canSaveWorkplan,
  toggleFetching,
  daysWithWork,
  deleteSingleEvidence,
  renderType,
  periods,
  monthSelected,
  setMonthSelected,
  compactAttendance
}) => {
  const validPeriods = React.useMemo(() => (...args) => validPeriod(args[3], periods), [periods]);
  return (
    <form onSubmit={handleSubmit}>
      {isFetching && <Loader />}
      <Grid fluid={true}>
        <Row>
          <Col xs={12} md={6}>
            <div>
              <DayPicker
                locale="cs"
                months={months}
                modifiers={modifiers}
                weekdaysLong={days}
                weekdaysShort={daysShort}
                firstDayOfWeek={1}
                selectedDays={selectedDay}
                showOutsideDays={false}
                onDayClick={(day, { disabled }) => {
                  if (!disabled) {
                    setSelectedDay(day);
                  }
                }}
                onMonthChange={value => {
                  setMonthSelected(value);
                }}
                month={monthSelected}
                disabledDays={{
                  after: moment()
                    .add(1, 'months')
                    .endOf('month')
                    .toDate(),
                  before: moment()
                    .subtract(1, 'months')
                    .startOf('month')
                    .toDate()
                }}
                renderDay={(day, { daysWithWork: daysWithWorkModifier }) => {
                  return (
                    <div
                      style={{
                        padding: '0.5rem',
                        background: daysWithWorkModifier ? '#22345f' : 'transparent'
                      }}
                    >
                      <div>{day.getDate()}</div>
                      {daysWithWorkModifier && renderType(day)}
                    </div>
                  );
                }}
              />
            </div>
          </Col>
          <Col xs={12} md={6}>
            <Row>
              <Col xs={12}>
                <RemoteAutoCompleteField
                  id="type"
                  name="type"
                  label="Typ pracovního dne"
                  component={RemoteAutoComplete}
                  url="/metropolis/api/hr/workplan/evidence/types"
                  labelFunction={o => get(o, 'name')}
                />
              </Col>
            </Row>

            <Row>
              <Col xs={12}>
                <InputField component={Input} id="note" name="note" label="Poznámka" />
              </Col>
            </Row>

            <Row>
              <Col xs={12}>
                <FieldArray<any>
                  rerenderOnEveryChange={true}
                  name="periods"
                  component={renderPeriods}
                  validPeriods={validPeriods}
                  compactAttendance={compactAttendance}
                />
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                {mode === 'creating' && (
                  <Button
                    type="submit"
                    label="Uložit"
                    primary={true}
                    disabled={!canSaveWorkplan}
                    style={{ marginTop: 24 }}
                  />
                )}
                {mode === 'updating' && (
                  <>
                    <Button
                      type="submit"
                      label="Editovat"
                      primary={true}
                      disabled={!canSaveWorkplan}
                      style={{ marginTop: 24, marginRight: 10 }}
                    />
                    <Button
                      label="Smazat"
                      style={{ marginTop: 24 }}
                      onClick={async () => {
                        if (confirm('Opravdu smazat záznam?')) {
                          toggleFetching(true);
                          const evidence = find(daysWithWork, {
                            ymd: moment.utc(selectedDay).format('YYYY-MM-DD')
                          });
                          deleteSingleEvidence(get(evidence, 'id'));
                        }
                      }}
                    />
                  </>
                )}
              </Col>
            </Row>
          </Col>
        </Row>
      </Grid>
    </form>
  );
};

export default enhance(View);



// WEBPACK FOOTER //
// ./src/modules/hr-overview/OverviewForm.tsx